import React, { useState, useEffect } from 'react';
import { 
  Wrench, Shield, Clock, CheckCircle, Award, Users, Phone, 
  ChevronDown, ArrowRight, Zap, Settings, ClipboardCheck,
  HardHat, Ruler, Sun, FileCheck, Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const ServicioInstalacionPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const processSteps = [
    {
      icon: ClipboardCheck,
      title: 'Evaluación Inicial',
      description: 'Visitamos tu propiedad para analizar el espacio, orientación del techo, consumo eléctrico y necesidades específicas.',
      duration: '1-2 días'
    },
    {
      icon: Ruler,
      title: 'Diseño del Sistema',
      description: 'Nuestros ingenieros diseñan un sistema personalizado optimizado para maximizar tu generación solar.',
      duration: '2-3 días'
    },
    {
      icon: FileCheck,
      title: 'Presupuesto y Aprobación',
      description: 'Te presentamos el proyecto completo con costos, retorno de inversión y opciones de financiamiento.',
      duration: '1 día'
    },
    {
      icon: HardHat,
      title: 'Instalación',
      description: 'Nuestro equipo certificado instala tu sistema con los más altos estándares de calidad y seguridad.',
      duration: '3-5 días'
    },
    {
      icon: Settings,
      title: 'Configuración',
      description: 'Configuramos el inversor, sistema de monitoreo y realizamos pruebas de funcionamiento.',
      duration: '1 día'
    },
    {
      icon: CheckCircle,
      title: 'Entrega y Capacitación',
      description: 'Te entregamos el sistema funcionando y te capacitamos en su uso y mantenimiento básico.',
      duration: '1 día'
    }
  ];

  const services = [
    {
      title: 'Instalación Residencial',
      description: 'Sistemas para hogares de cualquier tamaño, desde 3kW hasta 20kW.',
      features: ['Paneles de alta eficiencia', 'Inversor con garantía', 'Monitoreo incluido', 'Garantía de instalación'],
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600'
    },
    {
      title: 'Instalación Comercial',
      description: 'Soluciones para negocios, oficinas y comercios con alta demanda energética.',
      features: ['Sistemas de 20kW a 100kW', 'Optimización de espacio', 'Gestión con ANDE', 'ROI acelerado'],
      image: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=600'
    },
    {
      title: 'Instalación Industrial',
      description: 'Plantas solares de gran escala para industrias y fábricas.',
      features: ['Sistemas de 100kW+', 'Ingeniería especializada', 'Financiamiento industrial', 'Beneficios fiscales'],
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600'
    },
    {
      title: 'Instalación Off-Grid',
      description: 'Sistemas autónomos con baterías para zonas sin acceso a la red eléctrica.',
      features: ['Independencia total', 'Baterías de litio', 'Generador de respaldo', 'Soporte remoto'],
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600'
    }
  ];

  const guarantees = [
    { icon: Sun, title: '25 años', subtitle: 'Garantía en paneles solares' },
    { icon: Zap, title: '10 años', subtitle: 'Garantía en inversores' },
    { icon: Wrench, title: '5 años', subtitle: 'Garantía de instalación' },
    { icon: Shield, title: 'Lifetime', subtitle: 'Soporte técnico' }
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo demora una instalación completa?',
      answer: 'Desde la visita inicial hasta la puesta en marcha, el proceso completo toma entre 2 y 4 semanas dependiendo del tamaño del sistema. La instalación física en sí toma entre 3 y 5 días para sistemas residenciales.'
    },
    {
      question: '¿Necesito hacer modificaciones en mi casa?',
      answer: 'En la mayoría de los casos, no se requieren modificaciones estructurales. Evaluamos la capacidad de tu techo y realizamos los refuerzos necesarios si hace falta, todo incluido en el presupuesto.'
    },
    {
      question: '¿Qué pasa si mi techo no es ideal?',
      answer: 'Tenemos soluciones para todo tipo de techos: tejas, chapa, losa, y también instalaciones en suelo o con estructuras elevadas. Optimizamos el diseño para maximizar la generación.'
    },
    {
      question: '¿El sistema funciona durante cortes de luz?',
      answer: 'Los sistemas conectados a red (on-grid) se apagan durante cortes por seguridad. Si necesitás energía durante cortes, ofrecemos sistemas híbridos con baterías de respaldo.'
    },
    {
      question: '¿Quién realiza la instalación?',
      answer: 'Contamos con un equipo de instaladores propios, certificados y con años de experiencia. No subcontratamos la instalación para garantizar la calidad y responsabilidad directa.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <Wrench className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Servicio de Instalación</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Instalación Profesional
              <span className="text-[#90EE90]"> Certificada</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Nuestro equipo de expertos se encarga de todo el proceso, desde el diseño hasta la puesta en marcha. 
              Calidad garantizada y soporte permanente.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/calculadora')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Solicitar Presupuesto
                <ArrowRight size={20} />
              </button>
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20el%20servicio%20de%20instalación%20solar"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                <Phone size={20} />
                Contactar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees Bar */}
      <section className="py-8 bg-zinc-900 border-y border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {guarantees.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-8 h-8 text-[#90EE90] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{item.title}</p>
                  <p className="text-xs text-zinc-400">{item.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Types */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Tipos de Instalación
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Soluciones para Cada Necesidad
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:shadow-xl transition-all duration-300">
                <div className="h-48 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{service.title}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                        <CheckCircle className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestro Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              De Principio a Fin, Nos Encargamos
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Un proceso transparente y organizado para que no tengas que preocuparte por nada
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-lg transition-all">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#90EE90] rounded-full flex items-center justify-center text-zinc-900 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="p-3 bg-zinc-100 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-zinc-700" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-zinc-600 mb-3">{step.description}</p>
                  <div className="flex items-center gap-2 text-xs text-[#14532D] font-medium">
                    <Clock className="w-4 h-4" />
                    {step.duration}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
                ¿Por qué Zenex?
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                Instaladores Propios y Certificados
              </h2>
              <p className="text-zinc-400 mb-8">
                A diferencia de otras empresas, no subcontratamos. Nuestro equipo de instalación es propio, 
                capacitado y con años de experiencia en Paraguay.
              </p>
              
              <div className="space-y-4">
                {[
                  'Equipo técnico 100% propio',
                  'Capacitación continua en nuevas tecnologías',
                  'Responsabilidad directa sobre la instalación',
                  'Soporte post-venta garantizado'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600"
                alt="Equipo de instalación"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#90EE90] p-4 rounded-xl">
                <Users className="w-8 h-8 text-zinc-900 mb-2" />
                <p className="text-2xl font-bold text-zinc-900">50+</p>
                <p className="text-xs text-zinc-700">Instaladores certificados</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre la Instalación
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#14532D] to-[#166534]">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Wrench className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para Instalar tu Sistema Solar?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Solicitá una visita técnica gratuita y obtené tu presupuesto personalizado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors"
            >
              Calcular mi Sistema
            </button>
            <a
              href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20agendar%20una%20visita%20técnica"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Agendar Visita
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicioInstalacionPage;
